import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail, MapPin, Clock, MessageCircle } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { BUSINESS, SERVICES, whatsappUrl, pick } from "@/data/site";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function LangToggle({ compact = false }) {
  const { lang, setLang } = useLang();
  const base = "px-3 py-1.5 text-xs font-bold rounded-full transition-colors duration-200";
  return (
    <div
      className={`flex items-center gap-0.5 border border-slate-300 rounded-full p-0.5 bg-white ${compact ? "" : "ml-2"}`}
      data-testid="language-toggle"
    >
      <button
        data-testid="lang-en-button"
        onClick={() => setLang("en")}
        className={`${base} ${lang === "en" ? "bg-teal-600 text-white shadow-sm" : "text-slate-600 hover:text-teal-700"}`}
        aria-pressed={lang === "en"}
      >
        EN
      </button>
      <button
        data-testid="lang-te-button"
        onClick={() => setLang("te")}
        className={`${base} ${lang === "te" ? "bg-orange-500 text-white shadow-sm" : "text-slate-600 hover:text-orange-600"}`}
        aria-pressed={lang === "te"}
      >
        తెలుగు
      </button>
    </div>
  );
}

function Logo() {
  const { t } = useLang();
  return (
    <Link to="/" data-testid="brand-logo-link" className="flex items-center gap-3">
      <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-500 text-white flex items-center justify-center rounded-xl font-heading font-extrabold text-sm tracking-tight shadow-md shadow-orange-500/30">
        VBS
      </div>
      <div className="leading-tight">
        <div className="font-heading font-bold text-slate-900 text-base sm:text-lg">V.B. Sharada Associates</div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-orange-600 font-semibold">{t("brand.sub")}</div>
      </div>
    </Link>
  );
}

function Header() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { t } = useLang();
  useEffect(() => setOpen(false), [pathname]);

  const NAV = [
    { to: "/", label: t("nav.home"), key: "home" },
    { to: "/services", label: t("nav.services"), key: "services" },
    { to: "/projects", label: t("nav.projects"), key: "projects" },
    { to: "/about", label: t("nav.about"), key: "about-us" },
    { to: "/contact", label: t("nav.contact"), key: "contact" },
  ];

  return (
    <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-orange-100">
      <div className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto flex items-center justify-between h-16 sm:h-20">
        <Logo />
        <nav className="hidden md:flex items-center gap-1" data-testid="desktop-nav">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-link-${item.key}`}
              className={({ isActive }) =>
                `px-4 py-2 text-sm font-semibold rounded-full transition-colors duration-200 ${
                  isActive ? "bg-orange-500 text-white shadow-md shadow-orange-500/25" : "text-slate-600 hover:text-orange-600 hover:bg-orange-50"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <LangToggle />
          <a
            href={`tel:${BUSINESS.phone.replace(/\s/g, "")}`}
            data-testid="header-call-button"
            className="ml-3 inline-flex items-center gap-2 bg-teal-600 text-white text-sm font-semibold px-4 py-2 rounded-full hover:bg-teal-700 transition-colors duration-200 shadow-md shadow-teal-600/25"
          >
            <Phone className="w-4 h-4" /> {BUSINESS.phone}
          </a>
        </nav>
        <div className="md:hidden flex items-center gap-3">
          <LangToggle compact />
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="p-2 text-slate-700"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      {open && (
        <nav className="md:hidden border-t border-orange-100 bg-white px-4 py-3 space-y-1" data-testid="mobile-nav">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`mobile-nav-link-${item.key}`}
              className={({ isActive }) =>
                `block px-4 py-3 text-sm font-semibold rounded-full ${
                  isActive ? "bg-orange-500 text-white" : "text-slate-600"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <a
            href={`tel:${BUSINESS.phone.replace(/\s/g, "")}`}
            data-testid="mobile-call-button"
            className="flex items-center gap-2 px-4 py-3 text-sm font-semibold text-teal-700"
          >
            <Phone className="w-4 h-4" /> {BUSINESS.phone}
          </a>
        </nav>
      )}
    </header>
  );
}

function Footer() {
  const { t, lang } = useLang();
  const NAV = [
    { to: "/", label: t("nav.home"), key: "home" },
    { to: "/services", label: t("nav.services"), key: "services" },
    { to: "/projects", label: t("nav.projects"), key: "projects" },
    { to: "/about", label: t("nav.about"), key: "about-us" },
    { to: "/contact", label: t("nav.contact"), key: "contact" },
  ];
  return (
    <footer className="bg-slate-900 text-slate-300 blueprint-grid-dark" data-testid="site-footer">
      <div className="h-1.5 bg-gradient-to-r from-orange-500 via-amber-500 to-teal-500" />
      <div className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto py-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-500 text-white flex items-center justify-center rounded-xl font-heading font-extrabold text-sm shadow-md shadow-orange-500/30">
              VBS
            </div>
            <div className="font-heading font-bold text-white">V.B. Sharada Associates</div>
          </div>
          <p className="text-sm text-slate-400 leading-relaxed">{pick(BUSINESS, "description", lang)}</p>
        </div>
        <div>
          <h4 className="font-mono text-xs uppercase tracking-widest text-orange-400 font-semibold mb-4">{t("footer.quickLinks")}</h4>
          <ul className="space-y-2 text-sm">
            {NAV.map((item) => (
              <li key={item.to}>
                <Link
                  to={item.to}
                  data-testid={`footer-link-${item.key}`}
                  className="hover:text-orange-400 transition-colors duration-200"
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="font-mono text-xs uppercase tracking-widest text-teal-400 font-semibold mb-4">{t("footer.services")}</h4>
          <ul className="space-y-2 text-sm text-slate-400">
            {SERVICES.map((s) => (
              <li key={s.id}>{pick(s, "title", lang)}</li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="font-mono text-xs uppercase tracking-widest text-amber-400 font-semibold mb-4">{t("footer.reachUs")}</h4>
          <ul className="space-y-3 text-sm">
            <li className="flex gap-2">
              <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-orange-400" />
              <span className="text-slate-400">{BUSINESS.address}</span>
            </li>
            <li className="flex gap-2 items-center">
              <Phone className="w-4 h-4 shrink-0 text-teal-400" />
              <a href={`tel:${BUSINESS.phone.replace(/\s/g, "")}`} className="hover:text-white">
                {BUSINESS.phone}
              </a>
            </li>
            <li className="flex gap-2 items-center">
              <Mail className="w-4 h-4 shrink-0 text-amber-400" />
              <a href={`mailto:${BUSINESS.email}`} className="hover:text-white break-all">
                {BUSINESS.email}
              </a>
            </li>
            <li className="flex gap-2 items-center">
              <Clock className="w-4 h-4 shrink-0 text-orange-400" />
              <span className="text-slate-400">{pick(BUSINESS, "hours", lang)}</span>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-slate-800">
        <div className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto py-5 flex flex-col sm:flex-row justify-between items-center gap-2 text-xs text-slate-500">
          <span>© {new Date().getFullYear()} {t("footer.rights")}</span>
          <span className="font-mono uppercase tracking-widest text-orange-400/70">{t("footer.tagline")}</span>
        </div>
      </div>
    </footer>
  );
}

export default function Layout() {
  return (
    <div className="min-h-screen flex flex-col">
      <ScrollToTop />
      <Header />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
      <a
        href={whatsappUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="whatsapp-float-button"
        aria-label="Chat on WhatsApp"
        className="fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-emerald-500 text-white flex items-center justify-center shadow-lg shadow-emerald-500/40 hover:bg-emerald-600 transition-colors duration-200 animate-pulse-ring"
      >
        <MessageCircle className="w-7 h-7" />
      </a>
    </div>
  );
}
